/**
 * What the land did, in words (owner, 2026-09-28: "Summary shall provide the description of Land use
 * change ... get this autogenerated").
 *
 * The question is not "did greenness move" - a crop field swings from bare to full cover twice a
 * year - but "does the plot still look like the same use". So a fall is judged by what follows it:
 *   * a fall that RECOVERS (back to at least half of what it lost) is a crop cycle;
 *   * a fall back to where the season STARTED, after a green-up from that low, is a harvest;
 *   * anything else - a fall below the plot's own earlier low, or one that never comes back - is a
 *     possible land use change, named with its dates for somebody to check.
 * Too few clear scenes is its own answer: "cannot tell", never a quiet "no change".
 *
 * Only the plot's own boundary is read. The neighbouring fields are in the pictures, but nothing here
 * measures them, so the words never claim that they agree.
 */

export interface LandUsePoint { date: string; ndvi: number | null; usable: boolean }
export interface LandUseEvent {
  date: string; type: string; change_pct: number | string | null; severity: string; review_status: string;
  explained_by: string | null; reasons: string[] | null;
}
export interface LandUseInput {
  windowFrom: string; windowTo: string;
  /** The boundary's optical scenes, from before the dates (for where the season began) to their end. */
  observations: LandUsePoint[];
  events: LandUseEvent[];
  /** When the boundary's clear record begins. */
  historyFrom: string | null;
  mappedHa: number | null; declaredHa: number | null;
}

export type LandUseVerdict = 'none' | 'possible' | 'cannot_tell';

export interface LandUseSummary {
  verdict: LandUseVerdict;
  headline: string;
  finding: string;
  /** What was seen, period by period. */
  seen: string[];
  /** Why the verdict reads as it does. */
  why: string[];
  /** The changes the rules flagged, explained. */
  flags: string[];
  limits: string[];
}

/** A fall this large (share of the peak, and in NDVI) is worth judging. */
const MATERIAL_FRACTION = 0.35;
const MATERIAL_ABSOLUTE = 0.15;
/** Recovered: back up by at least half of what was lost. */
const RECOVERY_SHARE = 0.5;
/** "Back where it started": within this of the low the green-up began from. */
const START_TOLERANCE = 0.08;
const FEWEST_SCENES = 4;

const n2 = (v: number) => v.toFixed(2);
const DAY = 86_400_000;
const time = (iso: string) => new Date(`${iso}T00:00:00Z`).getTime();
const dayMonth = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const monthName = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' });
const monthSpan = (a: string, b: string) => (monthName(a) === monthName(b) ? monthName(a) : `${monthName(a)} - ${monthName(b)}`);
const list = (items: string[]) => (items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`);

interface Clear { date: string; ndvi: number }
interface Drop {
  peak: Clear; trough: Clear; start: Clear | null; recoveredOn: Clear | null; backToStart: boolean; regrowth: Clear | null;
  /** The last day at the low before the green-up, and the last day still near the peak before the fall. */
  riseFrom: Clear | null; fallFrom: Clear;
}

/** Each material fall: from the highest point since the last low to the lowest point before it rises again. */
function dropsIn(points: Clear[]): Drop[] {
  const drops: Drop[] = [];
  let peakIndex = 0;
  let i = 1;
  while (i < points.length) {
    if (points[i]!.ndvi > points[peakIndex]!.ndvi) { peakIndex = i; i += 1; continue; }
    const peak = points[peakIndex]!;
    const fall = peak.ndvi - points[i]!.ndvi;
    if (fall >= MATERIAL_ABSOLUTE && fall >= peak.ndvi * MATERIAL_FRACTION) {
      // Follow it down to its lowest point, stopping once it has clearly turned up again.
      let troughIndex = i;
      let j = i + 1;
      while (j < points.length && points[j]!.ndvi < points[troughIndex]!.ndvi + 0.1) {
        if (points[j]!.ndvi < points[troughIndex]!.ndvi) troughIndex = j;
        j += 1;
      }
      const trough = points[troughIndex]!;
      const before = points.slice(0, peakIndex).filter((p) => time(peak.date) - time(p.date) <= 180 * DAY);
      const start = before.length ? before.reduce((low, p) => (p.ndvi < low.ndvi ? p : low)) : null;
      const after = points.slice(troughIndex + 1);
      const recoveredOn = after.find((p) => p.ndvi >= trough.ndvi + RECOVERY_SHARE * (peak.ndvi - trough.ndvi)) ?? null;
      const regrowth = after.find((p) => p.ndvi >= trough.ndvi + 0.05) ?? null;
      const backToStart = start != null && peak.ndvi - start.ndvi >= MATERIAL_ABSOLUTE && trough.ndvi >= start.ndvi - START_TOLERANCE;
      const riseFrom = start ? [...before].reverse().find((p) => p.ndvi <= start.ndvi + 0.03) ?? start : null;
      const fallFrom = [...points.slice(peakIndex, troughIndex)].reverse().find((p) => p.ndvi >= peak.ndvi - 0.05) ?? peak;
      drops.push({ peak, trough, start, recoveredOn, backToStart, regrowth, riseFrom, fallFrom });
      peakIndex = troughIndex;
      i = troughIndex + 1;
      continue;
    }
    i += 1;
  }
  return drops;
}

/** Months in the dates with no clear scene at all - the gaps cloud left. */
function blindMonths(clear: Clear[], from: string, to: string): string[] {
  const seen = new Set(clear.map((p) => p.date.slice(0, 7)));
  const out: string[] = [];
  const cursor = new Date(`${from.slice(0, 7)}-01T00:00:00Z`);
  const end = to.slice(0, 7);
  while (cursor.toISOString().slice(0, 7) <= end) {
    const key = cursor.toISOString().slice(0, 7);
    if (!seen.has(key)) out.push(monthName(`${key}-01`));
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return out;
}

function flagLines(events: LandUseEvent[]): string[] {
  if (events.length === 0) return ['No change was flagged in these dates.'];
  const gains = events.filter((e) => e.type === 'vegetation_gain');
  const losses = events.filter((e) => e.type === 'vegetation_loss');
  const other = events.filter((e) => e.type !== 'vegetation_gain' && e.type !== 'vegetation_loss');
  const pct = (e: LandUseEvent) => (e.change_pct == null ? '' : ` (${Number(e.change_pct) > 0 ? '+' : ''}${Math.round(Number(e.change_pct))}%)`);
  const lines: string[] = [];
  if (gains.length) lines.push(`${list(gains.map((e) => `${dayMonth(e.date)}${pct(e)}`))}: greening - a crop coming up.`);
  if (losses.length) {
    const unexplained = losses.filter((e) => !e.explained_by);
    const critical = unexplained.filter((e) => e.severity === 'critical');
    lines.push(`${list(losses.map((e) => `${dayMonth(e.date)}${pct(e)}`))}: vegetation falling${losses.length === unexplained.length ? '' : ' (some explained by a declared activity)'}.`
      + (critical.length ? ` ${list(critical.map((e) => dayMonth(e.date)))} ${critical.length === 1 ? 'is' : 'are'} marked critical because no declared harvest covers ${critical.length === 1 ? 'it' : 'them'}; declaring the harvest would explain ${critical.length === 1 ? 'it' : 'them'}.` : ''));
  }
  if (other.length) lines.push(`${list(other.map((e) => `${dayMonth(e.date)} ${e.type.replace(/_/g, ' ')}`))}.`);
  const open = events.filter((e) => e.review_status === 'potential' || e.review_status === 'under_review').length;
  lines.push(open ? `${open} of ${events.length} still ${open === 1 ? 'needs' : 'need'} a person to review ${open === 1 ? 'it' : 'them'}.` : 'All have been reviewed.');
  return lines;
}

export function landUseSummary(input: LandUseInput): LandUseSummary {
  const clearAll: Clear[] = input.observations
    .filter((p) => p.usable && p.ndvi != null)
    .map((p) => ({ date: p.date, ndvi: Number(p.ndvi) }))
    .sort((a, b) => a.date.localeCompare(b.date));
  const inside = clearAll.filter((p) => p.date >= input.windowFrom && p.date <= input.windowTo);
  const clouded = input.observations.filter((p) => !p.usable && p.date >= input.windowFrom && p.date <= input.windowTo).length;

  const limits: string[] = [`Judged on ${inside.length} clear scene${inside.length === 1 ? '' : 's'} of this boundary${clouded ? ` (${clouded} more hidden by cloud)` : ''}.`];
  const blind = blindMonths(inside, input.windowFrom, input.windowTo);
  if (blind.length) limits.push(`No clear scene in ${list(blind)}: cloud hid the plot.`);
  if (!input.historyFrom || time(input.windowTo) - time(input.historyFrom) < 330 * DAY) {
    limits.push('Less than a year of satellite history for this boundary, so there is no earlier year to compare the season with.');
  }
  if (input.mappedHa != null && input.declaredHa != null && input.declaredHa > 0
      && Math.abs(input.mappedHa - input.declaredHa) / input.declaredHa > 0.1) {
    limits.push(`The boundary covers ${input.mappedHa.toFixed(2)} ha against ${input.declaredHa.toFixed(2)} ha declared.`);
  }
  limits.push('Satellite evidence is a reason to check the field records, never a finding on its own.');

  if (inside.length < FEWEST_SCENES) {
    return {
      verdict: 'cannot_tell',
      headline: 'Land use change: cannot tell',
      finding: `Only ${inside.length} clear scene${inside.length === 1 ? '' : 's'} in these dates - too few to say how the plot is used.`,
      seen: [], why: [], flags: flagLines(input.events), limits,
    };
  }

  // The dates, plus up to two months before them: a season that began before the dates still began somewhere.
  const drops = dropsIn(clearAll).filter((d) => d.trough.date >= input.windowFrom);
  const last = inside[inside.length - 1]!;
  const low = inside.reduce((a, p) => (p.ndvi < a.ndvi ? p : a));
  const high = inside.reduce((a, p) => (p.ndvi > a.ndvi ? p : a));
  const flags = flagLines(input.events);
  const seen: string[] = [];
  const why: string[] = [];

  if (drops.length === 0) {
    seen.push(`${monthSpan(inside[0]!.date, last.date)}: vegetation stayed between ${n2(low.ndvi)} and ${n2(high.ndvi)}.`);
    if (blind.length) seen.push(`${list(blind)}: hidden by cloud; no reading.`);
    why.push('No fall large enough to matter: the plot looked the same throughout.');
    return {
      verdict: 'none', headline: 'Land use change: none indicated',
      finding: `Vegetation held steady (NDVI ${n2(low.ndvi)} - ${n2(high.ndvi)}) through the dates. Nothing suggests the land was cleared or put to another use.`,
      seen, why, flags, limits,
    };
  }

  const unexplained = drops.filter((d) => !d.recoveredOn && !d.backToStart);
  for (const d of drops) {
    if (d.riseFrom && d.backToStart) {
      seen.push(`${monthSpan(d.riseFrom.date, d.peak.date)}: a crop grows to full cover (${n2(d.riseFrom.ndvi)} on ${dayMonth(d.riseFrom.date)} to ${n2(d.peak.ndvi)} on ${dayMonth(d.peak.date)})`
        + `${d.fallFrom.date !== d.peak.date ? ` and stays there until ${dayMonth(d.fallFrom.date)}` : ''}.`);
    } else {
      seen.push(`To ${dayMonth(d.fallFrom.date)}: vegetation at ${n2(d.peak.ndvi)}.`);
    }
    seen.push(`${monthSpan(d.fallFrom.date, d.trough.date)}: falls to ${n2(d.trough.ndvi)} by ${dayMonth(d.trough.date)}${d.backToStart ? ' - back where the season began, as a harvest leaves a field' : ''}.`);
    if (d.recoveredOn) seen.push(`${monthSpan(d.trough.date, d.recoveredOn.date)}: green again (${n2(d.recoveredOn.ndvi)} on ${dayMonth(d.recoveredOn.date)}).`);
    else if (d.regrowth) seen.push(`From ${dayMonth(d.regrowth.date)}: greening again (${n2(d.regrowth.ndvi)}).`);
  }
  if (blind.length) seen.push(`${list(blind)}: hidden by cloud; no reading.`);

  if (unexplained.length === 0) {
    if (drops.some((d) => d.recoveredOn)) why.push('It recovers: every fall is followed by green growth again. Cleared or converted land does not green up again within a season.');
    if (drops.some((d) => d.backToStart)) why.push('The fall returns the field to the level it started the season at, after a green-up from there - the shape of a crop grown and harvested, not of land taken out of use.');
    if (drops.some((d) => !d.recoveredOn && d.regrowth)) why.push(`It is greening again by ${dayMonth(drops.find((d) => !d.recoveredOn && d.regrowth)!.regrowth!.date)}.`);
    return {
      verdict: 'none', headline: 'Land use change: none indicated',
      finding: 'The plot shows a seasonal cropping cycle in these dates. Nothing suggests the land was converted to another use (cleared of tree cover, built on, flooded or left permanently bare).',
      seen, why, flags, limits,
    };
  }

  const d = unexplained[0]!;
  why.push(`Vegetation fell from ${n2(d.peak.ndvi)} (${dayMonth(d.fallFrom.date)}) to ${n2(d.trough.ndvi)} (${dayMonth(d.trough.date)})`
    + (d.start ? `, below the ${n2(d.start.ndvi)} the plot started from,` : '')
    + ` and had not come back by the last clear scene (${n2(last.ndvi)} on ${dayMonth(last.date)}).`);
  why.push('A clearance, a building, a new water body or a change of crop would show like this - so would a long fallow. The field records decide which.');
  return {
    verdict: 'possible', headline: 'Land use change: possible - check on the ground',
    finding: `Vegetation fell by ${Math.round(((d.peak.ndvi - d.trough.ndvi) / d.peak.ndvi) * 100)}% between ${dayMonth(d.fallFrom.date)} and ${dayMonth(d.trough.date)} and did not recover in these dates.`,
    seen, why, flags, limits,
  };
}
